const COMPARE_STORAGE_KEY = 'theme:compare-products';
const COMPARE_MAX_PRODUCTS = 2;
const COMPARE_UPDATED_EVENT = 'compare:updated';
const COMPARE_ALL_PRODUCTS_COLLECTION = 'all';

function compareFilterProductsByQuery(products, query) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return products;
  return products.filter((product) => product.title.toLowerCase().includes(normalizedQuery));
}

function compareGetHandles() {
  try {
    const raw = localStorage.getItem(COMPARE_STORAGE_KEY);
    const handles = raw ? JSON.parse(raw) : [];
    return Array.isArray(handles) ? handles.filter((handle) => typeof handle === 'string') : [];
  } catch (error) {
    return [];
  }
}

function compareSetHandles(handles) {
  localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(handles));
  document.dispatchEvent(new CustomEvent(COMPARE_UPDATED_EVENT, { detail: { handles } }));
}

function compareHasHandle(handle) {
  return compareGetHandles().includes(handle);
}

function compareIsFull() {
  return compareGetHandles().length >= COMPARE_MAX_PRODUCTS;
}

function compareAddHandle(handle) {
  const handles = compareGetHandles();
  if (handles.includes(handle) || handles.length >= COMPARE_MAX_PRODUCTS) {
    return { handles, added: false, full: handles.length >= COMPARE_MAX_PRODUCTS };
  }
  const nextHandles = [...handles, handle];
  compareSetHandles(nextHandles);
  return { handles: nextHandles, added: true, full: nextHandles.length >= COMPARE_MAX_PRODUCTS };
}

function compareRemoveHandle(handle) {
  const nextHandles = compareGetHandles().filter((existingHandle) => existingHandle !== handle);
  compareSetHandles(nextHandles);
  return nextHandles;
}

function compareClearAll() {
  compareSetHandles([]);
}

const compareCardHtmlCache = new Map();
const compareSelectCardHtmlCache = new Map();

function compareFetchProductCardHTML(handle) {
  if (compareCardHtmlCache.has(handle)) {
    return compareCardHtmlCache.get(handle);
  }
  const request = fetch(`/products/${handle}`)
    .then((response) => response.text())
    .then((responseText) => {
      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceCard = doc.querySelector('.compare-drawer__source-card');
      return sourceCard ? sourceCard.innerHTML : '';
    });
  compareCardHtmlCache.set(handle, request);
  return request;
}

function compareFetchProductSelectCardHTML(handle) {
  if (compareSelectCardHtmlCache.has(handle)) {
    return compareSelectCardHtmlCache.get(handle);
  }
  const request = fetch(`/products/${handle}`)
    .then((response) => response.text())
    .then((responseText) => {
      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceCard = doc.querySelector('.compare-drawer__source-card-select');
      return sourceCard ? sourceCard.innerHTML : '';
    });
  compareSelectCardHtmlCache.set(handle, request);
  return request;
}

class CompareDrawer extends ModalDialog {
  constructor() {
    super();
    this.screens = {
      select: this.querySelector('[data-compare-screen="select"]'),
      category: this.querySelector('[data-compare-screen="category"]'),
      search: this.querySelector('[data-compare-screen="search"]'),
      table: this.querySelector('[data-compare-screen="table"]'),
    };
    this.slotsContainer = this.querySelector('[data-compare-slots]');
    this.loadingIndicator = this.querySelector('[data-compare-loading]');
    this.backButton = this.querySelector('[data-compare-back]');
    this.categoriesGrid = this.querySelector('.compare-drawer__categories');
    this.globalSearchInput = this.querySelector('[data-compare-global-search-input]');
    this.globalResultsList = this.querySelector('[data-compare-global-results]');
    this.renderToken = 0;
    if (this.globalSearchInput) {
      let globalSearchDebounce;
      this.globalSearchInput.addEventListener('input', () => {
        clearTimeout(globalSearchDebounce);
        globalSearchDebounce = setTimeout(() => {
          const query = this.globalSearchInput.value.trim();
          if (!query) {
            if (this.categoriesGrid) this.categoriesGrid.hidden = false;
            if (this.globalResultsList) this.globalResultsList.hidden = true;
            return;
          }
          if (this.categoriesGrid) this.categoriesGrid.hidden = true;
          if (this.globalResultsList) this.globalResultsList.hidden = false;
          this.searchAllProducts(query, this.globalResultsList);
        }, ON_CHANGE_DEBOUNCE_TIMER);
      });
    }
    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener(COMPARE_UPDATED_EVENT, () => {
      this.syncTriggers();
      this.announceCountChange(compareGetHandles().length);
    });
    this.querySelectorAll('[data-compare-clear-all]').forEach((button) => {
      button.addEventListener('click', () => {
        compareClearAll();
        this.showScreenForCurrentState();
      });
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.syncTriggers();
  }

  onDocumentClick(event) {
    const toggle = event.target.closest('[data-compare-toggle]');
    const opener = event.target.closest('[data-compare-open]');
    const addSlot = event.target.closest('[data-compare-add-slot]');
    const removeSlot = event.target.closest('[data-compare-remove-slot]');
    const category = event.target.closest('[data-compare-category]');
    const select = event.target.closest('[data-compare-select]');
    const back = event.target.closest('[data-compare-back]');

    if (toggle) {
      event.preventDefault();
      this.handleToggle(toggle);
      return;
    }
    if (addSlot && this.contains(addSlot)) {
      event.preventDefault();
      this.showScreen('category');
      return;
    }
    if (back && this.contains(back)) {
      event.preventDefault();
      if (this.activeScreen === 'search') {
        this.showScreen('category');
      } else {
        this.showScreenForCurrentState();
      }
      return;
    }
    if (removeSlot && this.contains(removeSlot)) {
      event.preventDefault();
      compareRemoveHandle(removeSlot.dataset.productHandle);
      this.showScreenForCurrentState();
      return;
    }
    if (category && this.contains(category)) {
      event.preventDefault();
      this.openSearchForCollection(category.dataset.collectionHandle);
      return;
    }
    if (select && this.contains(select)) {
      event.preventDefault();
      compareAddHandle(select.dataset.productHandle);
      this.showScreenForCurrentState();
      return;
    }
    if (opener) {
      event.preventDefault();
      this.show(opener);
      this.showScreenForCurrentState();
    }
  }

  openSearchForCollection(collectionHandle) {
    this.showScreen('search');
    const searchInput = this.screens.search.querySelector('[data-compare-search-input]');
    const resultsList = this.screens.search.querySelector('[data-compare-results]');
    resultsList.innerHTML = '';
    searchInput.value = '';
    if (this.loadingIndicator) this.loadingIndicator.hidden = false;

    fetch(`/collections/${collectionHandle}/products.json?limit=250`)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Request failed'))))
      .then((data) => {
        this.currentCollectionProducts = data.products || [];
        this.renderSearchResults(this.currentCollectionProducts);
      })
      .catch(() => {
        this.currentCollectionProducts = [];
        this.renderSearchResults([]);
      });

    let debounceTimer;
    searchInput.oninput = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const query = searchInput.value.trim();
        if (!query) {
          this.renderSearchResults(this.currentCollectionProducts || []);
          return;
        }
        this.searchAllProducts(query, resultsList);
      }, ON_CHANGE_DEBOUNCE_TIMER);
    };
  }

  renderSearchResults(products) {
    const resultsList = this.screens.search.querySelector('[data-compare-results]');
    this.renderProductResults(products, resultsList);
  }

  fetchAllProducts() {
    if (!this.allProductsRequest) {
      this.allProductsRequest = fetch(`/collections/${COMPARE_ALL_PRODUCTS_COLLECTION}/products.json?limit=250`)
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Request failed'))))
        .then((data) => data.products || [])
        .catch(() => []);
    }
    return this.allProductsRequest;
  }

  searchAllProducts(query, resultsList) {
    if (!resultsList) return;
    this.fetchAllProducts().then((products) => {
      this.renderProductResults(compareFilterProductsByQuery(products, query), resultsList);
    });
  }

  renderProductResults(products, resultsList) {
    if (!resultsList) return;
    const resultTemplate = this.querySelector('[data-compare-result-template]');
    const noResultsTemplate = this.querySelector('[data-compare-no-results-template]');
    resultsList.innerHTML = '';
    if (products.length === 0) {
      if (this.loadingIndicator) this.loadingIndicator.hidden = true;
      if (noResultsTemplate) resultsList.appendChild(noResultsTemplate.content.cloneNode(true));
      return;
    }
    if (this.loadingIndicator) this.loadingIndicator.hidden = false;
    const token = ++this.renderToken;
    Promise.all(products.map((product) => compareFetchProductSelectCardHTML(product.handle))).then(
      (cardHtmls) => {
        if (token !== this.renderToken) return;
        if (this.loadingIndicator) this.loadingIndicator.hidden = true;
        resultsList.innerHTML = '';
        products.forEach((product, index) => {
          if (!resultTemplate) return;
          const item = resultTemplate.content.cloneNode(true);
          const cardContainer = item.querySelector('[data-compare-result-card]');
          if (cardContainer) cardContainer.innerHTML = cardHtmls[index];
          const isAdded = compareHasHandle(product.handle);
          const resultItem = item.querySelector('.compare-drawer__result');
          if (resultItem) resultItem.classList.toggle('light', isAdded);
          const selectButton = cardContainer ? cardContainer.querySelector('[data-compare-select]') : null;
          if (selectButton && isAdded) {
            selectButton.disabled = true;
            if (this.dataset.labelAdded) selectButton.textContent = this.dataset.labelAdded;
          }
          resultsList.appendChild(item);
        });
      }
    );
  }

  handleToggle(trigger) {
    const handle = trigger.dataset.productHandle;
    const mode = trigger.dataset.compareMode;

    if (mode === 'pdp') {
      if (compareHasHandle(handle)) {
        this.show(trigger);
        this.showScreenForCurrentState();
        return;
      }
      if (compareIsFull()) {
        this.show(trigger);
        this.showScreen('table');
        return;
      }
      compareAddHandle(handle);
      this.show(trigger);
      this.showScreenForCurrentState();
      return;
    }

    if (compareHasHandle(handle)) {
      compareRemoveHandle(handle);
    } else {
      compareAddHandle(handle);
    }
    this.show(trigger);
    this.showScreenForCurrentState();
  }

  showScreenForCurrentState() {
    this.showScreen(compareIsFull() ? 'table' : 'select');
  }

  showScreen(name) {
    Object.entries(this.screens).forEach(([screenName, element]) => {
      if (!element) return;
      element.hidden = screenName !== name;
    });
    this.activeScreen = name;
    if (this.backButton) this.backButton.hidden = name !== 'category' && name !== 'search';
    if (name === 'select') this.renderSelectScreen();
    if (name === 'table') this.renderTableScreen();
    if (name === 'category') this.resetGlobalSearch();
  }

  resetGlobalSearch() {
    if (this.globalSearchInput) this.globalSearchInput.value = '';
    if (this.globalResultsList) this.globalResultsList.hidden = true;
    if (this.categoriesGrid) this.categoriesGrid.hidden = false;
  }

  renderSelectScreen() {
    const handles = compareGetHandles();
    const addSlotTemplate = this.screens.select.querySelector('[data-compare-add-slot-template]');
    const filledSlotTemplate = this.screens.select.querySelector('[data-compare-filled-slot-template]');
    const clearAllButton = this.screens.select.querySelector('[data-compare-clear-all]');
    if (clearAllButton) clearAllButton.hidden = handles.length === 0;

    if (this.loadingIndicator) this.loadingIndicator.hidden = false;
    this.slotsContainer.hidden = true;
    const token = ++this.renderToken;
    Promise.all(handles.map((handle) => compareFetchProductCardHTML(handle))).then((cardHtmls) => {
      if (token !== this.renderToken) return;
      const invalidHandles = handles.filter((handle, index) => !cardHtmls[index]);
      if (invalidHandles.length > 0) {
        invalidHandles.forEach((handle) => compareRemoveHandle(handle));
        this.renderSelectScreen();
        return;
      }
      if (this.loadingIndicator) this.loadingIndicator.hidden = true;
      this.slotsContainer.hidden = false;
      this.slotsContainer.classList.toggle('compare-drawer__slots--empty', handles.length === 0);
      this.refocusAfterRender(this.slotsContainer);
      this.slotsContainer.innerHTML = '';
      for (let index = 0; index < COMPARE_MAX_PRODUCTS; index += 1) {
        const handle = handles[index];
        const slot = document.createElement('div');
        slot.className = 'compare-drawer__slot';
        if (handle && filledSlotTemplate) {
          const content = filledSlotTemplate.content.cloneNode(true);
          const removeButton = content.querySelector('[data-compare-remove-slot]');
          if (removeButton) removeButton.dataset.productHandle = handle;
          const cardContainer = content.querySelector('[data-compare-slot-card]');
          if (cardContainer) cardContainer.innerHTML = cardHtmls[index];
          slot.appendChild(content);
        } else if (addSlotTemplate) {
          slot.appendChild(addSlotTemplate.content.cloneNode(true));
        }
        this.slotsContainer.appendChild(slot);
      }
    });
  }

  renderTableScreen() {
    const handles = compareGetHandles();
    const cardsContainer = this.screens.table.querySelector('[data-compare-cards]');
    const cardTemplate = this.screens.table.querySelector('[data-compare-card-template]');

    if (this.loadingIndicator) this.loadingIndicator.hidden = false;
    cardsContainer.hidden = true;
    const token = ++this.renderToken;
    Promise.all(handles.map((handle) => compareFetchProductCardHTML(handle))).then((cardHtmls) => {
      if (token !== this.renderToken) return;
      const invalidHandles = handles.filter((handle, index) => !cardHtmls[index]);
      if (invalidHandles.length > 0) {
        invalidHandles.forEach((handle) => compareRemoveHandle(handle));
        this.renderTableScreen();
        return;
      }
      if (this.loadingIndicator) this.loadingIndicator.hidden = true;
      cardsContainer.hidden = false;
      this.refocusAfterRender(cardsContainer);
      cardsContainer.innerHTML = '';
      handles.forEach((handle, index) => {
        if (!cardTemplate) return;
        const card = document.createElement('div');
        card.className = 'compare-drawer__slot';
        const content = cardTemplate.content.cloneNode(true);
        const removeButton = content.querySelector('[data-compare-remove-slot]');
        if (removeButton) removeButton.dataset.productHandle = handle;
        const cardContainer = content.querySelector('[data-compare-slot-card]');
        if (cardContainer) cardContainer.innerHTML = cardHtmls[index];
        card.appendChild(content);
        cardsContainer.appendChild(card);
      });
    });
  }

  refocusAfterRender(container) {
    const active = document.activeElement;
    if (active && container.contains(active)) {
      const heading = this.querySelector('.compare-drawer__heading');
      if (heading) heading.focus();
    }
  }

  syncTriggers() {
    const handles = compareGetHandles();
    document.querySelectorAll('[data-compare-toggle]').forEach((trigger) => {
      const isActive = handles.includes(trigger.dataset.productHandle);
      trigger.setAttribute('aria-pressed', String(isActive));
      trigger.classList.toggle('is-active', isActive);
    });
    document.querySelectorAll('[data-compare-count]').forEach((badge) => {
      badge.textContent = String(handles.length);
      badge.hidden = handles.length === 0;
    });
  }

  announceCountChange(count) {
    const liveRegion = document.querySelector('[data-compare-live-region]');
    if (!liveRegion) return;
    const template = count === 1 ? this.dataset.labelCountOne : this.dataset.labelCountOther;
    if (!template) return;
    liveRegion.textContent = template.replace('[count]', String(count));
  }
}

if (!customElements.get('compare-drawer')) {
  customElements.define('compare-drawer', CompareDrawer);
}
